// Keep promises outside reactive Vuex state, isolated to each attempt module.
const queues = new WeakMap();

export function pendingAnswerSaves(state) {
    return [...(queues.get(state)?.values() ?? [])].map(queue => queue.tail);
}

export function answerFinalizationPending(state, id) {
    return queues.get(state)?.get(id)?.finalizing ?? false;
}

export async function drainAnswerSaves(state) {
    let saved = true;
    let pending = pendingAnswerSaves(state);
    while (pending.length) {
        const results = await Promise.all(pending);
        saved = results.every(result => result?.saved !== false) && saved;
        // An input event may have appended a write while the previous tail settled.
        pending = pendingAnswerSaves(state);
    }
    return saved;
}

export function serializeAnswerSave(state, id, save, { finalize } = {}) {
    let questions = queues.get(state);
    if (!questions) {
        questions = new Map();
        queues.set(state, questions);
    }
    let queue = questions.get(id);
    if (!queue) {
        queue = { tail: Promise.resolve(), draft: null, finalizing: false };
        questions.set(id, queue);
    }

    if (finalize) queue.finalizing = true;

    // Typing can outpace the network. Replace only a draft that has not started,
    // so a burst waits for at most the in-flight write and the latest draft.
    // A finalization is a barrier: edits after it cannot replace drafts before it.
    if (!finalize && queue.draft) {
        queue.draft.save = save;
        return queue.draft.promise;
    }

    // Preserve text emitted before the UI becomes read-only, but never turn the
    // pending finalization back into a draft. Reopening after this queue drains
    // starts a fresh queue and can save drafts normally.
    const job = { save, finalize: queue.finalizing, promise: null };
    const pending = queue.tail
        .catch(() => {})
        .then(() => {
            if (queue.draft === job) queue.draft = null;
            // Aborting an HTTP write cannot stop its server transaction. Always let
            // the previous response settle before sending the next write.
            return job.save({ finalize: job.finalize });
        });
    queue.tail = pending;
    queue.draft = finalize ? null : job;
    job.promise = pending.finally(() => {
        if (queue.tail === pending) questions.delete(id);
    });
    return job.promise;
}
